import { Fragment, type Slice } from "prosemirror-model";
import { NodeSelection } from "prosemirror-state";
import { type EditorView } from "prosemirror-view";
import { useCallback } from "react";

import { schema } from "@moment/api-collab/prosemirror-schema";

import { useEasel } from "../easel";
import { useComputeCells } from "./useComputeCells";

/**
 * Returns a callback intended to be passed to the `handleDrop` prop of the NYT `ProseMirror`
 * component. This callback will handle the drag-and-drop semantics for the editor, including
 * inserting templates.
 */
export const useEaselProseMirrorPasteHandler = () => {
	const { documentId, pageId } = useEasel();
	const { makeUniqueCellName } = useComputeCells(documentId, pageId);

	return useCallback(
		(view: EditorView, _e: ClipboardEvent, slice: Slice) => {
			// Proceed normally if we're not pasting a single `compute_cell`.
			const firstNode = slice.content.firstChild;
			if (slice.content.childCount !== 1 || firstNode?.type.name !== "compute_cell") {
				return false;
			}

			const attrs = firstNode.attrs;
			let tr = view.state.tr;

			// IMPORTANT: Backwards compatibility for when `code` was stored in Node attrs instead of
			// text content.
			const code = attrs["code"] || firstNode.textContent;
			// Don't collide with the cell we're copying from.
			const cellName = makeUniqueCellName(attrs["cellName"]);
			const cellToInsert = schema.nodes.compute_cell.create(
				{ ...attrs, cellName },
				Fragment.from(schema.text(code))
			);

			const sel = view.state.selection;
			let insertPos = sel.$head.end(sel.$head.depth);
			if (sel instanceof NodeSelection) {
				// `NodeSelection#end()` will returns the position of the parent node (often doc,
				// which would be 0).
				insertPos = sel.$head.pos;
			}

			const fromNode = sel.$from.node(sel.$from.depth);
			const toNode = sel.$to.node(sel.$to.depth);
			if (
				fromNode === toNode &&
				fromNode.type === schema.nodes.paragraph &&
				fromNode.content.size === 0
			) {
				tr = tr.delete(sel.from - 1, sel.to + 1);
			}
			tr = tr.insert(tr.mapping.map(insertPos), Fragment.from(cellToInsert));

			view.dispatch(tr);
			view.focus();

			return true;
		},
		[makeUniqueCellName]
	);
};
